import React, { Component } from 'react'
import { withFirebase } from '../../components/Fb'
import { Row, Col, Button } from 'reactstrap'
import {
  DataGrid,
  GridToolbarExport,
  GridToolbarContainer,
  GridToolbarFilterButton,
} from '@mui/x-data-grid'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import ShareRegistryInfo from '../../components/Infos/ShareRegistryInfo'

const service = 'shareregistry'

function ExportToolbar({ suffix }) {
  return (
    <GridToolbarContainer style={{ justifyContent: 'flex-start' }}>
      <GridToolbarFilterButton />
      <GridToolbarExport
        csvOptions={{
          fileName: 'osakerekisteri-' + (suffix || 'tapahtumat'),
          delimiter: ';',
          utf8WithBom: true,
        }}
      />
    </GridToolbarContainer>
  )
}

class ShareRegistryTransactionsContainer extends Component {
  constructor(props) {
    super(props)
    this.state = {
      enabled: false,
      saving: false,
      loading: true,
      activeTab: 0,
      transactions: [],
      deals: [],
    }
  }

  componentDidMount() {
    if (this.props.modules && this.props.modules[service] && this.props.modules[service].enabled) {
      this.setState({ enabled: true })
    }
    this.loadAllData()
  }

  componentDidUpdate(prevProps) {
    if (this.props.modules && this.props.modules[service]) {
      if (this.props.modules[service].enabled && !prevProps.modules[service]?.enabled) {
        this.setState({ enabled: true })
      }
      if (!this.props.modules[service].enabled && prevProps.modules[service]?.enabled) {
        this.setState({ enabled: false })
      }
    } else {
      if (this.state.enabled) {
        this.setState({ enabled: false })
      }
    }
  }

  enableModule = async (price) => {
    this.setState({ saving: true })
    await this.props.firebase.enableModule(service, price)
  }

  loadAllData = async () => {
    try {
      const [sharesSnap, holdersSnap, classesSnap] = await Promise.all([
        this.props.firebase.getShares(),
        this.props.firebase.getShareholders(),
        this.props.firebase.getShareClasses(),
      ])

      const shares = sharesSnap.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((r) => r.state !== 'deleted')

      const holders = holdersSnap.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((r) => r.state !== 'deleted')

      const classes = classesSnap.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((r) => r.state !== 'deleted')

      // Flatten transactions from all shares
      const transactions = []
      shares.forEach((share) => {
        if (share.transactions && Array.isArray(share.transactions)) {
          share.transactions.forEach((t) => {
            transactions.push({
              ...t,
              id: (t.id || transactions.length.toString()) + '-' + share.id,
              shareId: share.id,
              shareNumber: share.shareNumber,
              classId: share.classId,
            })
          })
        }
      })

      transactions.sort((a, b) => (b.date || '').localeCompare(a.date || ''))

      // Aggregate by dealId
      const dealMap = {}
      transactions.forEach((t) => {
        const key = t.dealId || t.id
        if (!dealMap[key]) {
          dealMap[key] = {
            id: key,
            dealId: t.dealId || '',
            date: t.date,
            type: t.type,
            fromId: t.fromId,
            toId: t.toId,
            price: t.price,
            note: t.note,
            classId: t.classId,
            shareNumbers: [],
            shareCount: 0,
          }
        }
        dealMap[key].shareNumbers.push(t.shareNumber)
        dealMap[key].shareCount++
      })

      const deals = Object.values(dealMap)
      deals.sort((a, b) => (b.date || '').localeCompare(a.date || ''))

      this.setState({ transactions, deals, holders, classes, loading: false })
    } catch (error) {
      console.error('Error loading transactions:', error)
      this.setState({ loading: false })
    }
  }

  handleTabChange = (event, newValue) => {
    this.setState({ activeTab: newValue })
  }

  getHolderName = (holderId) => {
    if (!holderId) return '-'
    const holder = this.state.holders.find((h) => h.id === holderId)
    if (!holder) return '-'
    if (holder.personName) return holder.personName
    if (holder.email) return holder.email
    return holder.id
  }

  getClassName = (classId) => {
    const cls = this.state.classes.find((c) => c.id === classId)
    return cls ? cls.name : '-'
  }

  formatShareRange = (numbers) => {
    if (!numbers || numbers.length === 0) return '-'
    const sorted = [...numbers].sort((a, b) => a - b)
    if (sorted.length === 1) return String(sorted[0])

    // Collapse consecutive numbers into ranges
    const ranges = []
    let start = sorted[0]
    let end = sorted[0]
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === end + 1) {
        end = sorted[i]
      } else {
        ranges.push(start === end ? String(start) : start + '-' + end)
        start = sorted[i]
        end = sorted[i]
      }
    }
    ranges.push(start === end ? String(start) : start + '-' + end)
    return ranges.join(', ')
  }

  getTransactionColumns = () => [
    { headerName: 'Päivämäärä', field: 'date', minWidth: 130 },
    {
      headerName: 'Kauppatunnus',
      field: 'dealId',
      minWidth: 130,
      valueGetter: (value) => value ? value.substring(0, 8) : '-',
    },
    {
      headerName: 'Tyyppi',
      field: 'type',
      minWidth: 100,
      valueGetter: (value) => value === 'buy' ? 'Osto' : value === 'sell' ? 'Myynti' : value || '-',
    },
    { headerName: 'Osakenumero', field: 'shareNumber', minWidth: 130 },
    {
      headerName: 'Osakelajit',
      field: 'classId',
      minWidth: 150,
      valueGetter: (value) => this.getClassName(value),
    },
    {
      headerName: 'Myyjä',
      field: 'fromId',
      minWidth: 200,
      valueGetter: (value) => this.getHolderName(value),
    },
    {
      headerName: 'Ostaja',
      field: 'toId',
      minWidth: 200,
      valueGetter: (value) => this.getHolderName(value),
    },
    {
      headerName: 'Hinta/osake (€)',
      field: 'price',
      minWidth: 130,
      valueGetter: (value) => value != null ? value : '-',
    },
    { headerName: 'Muistiinpano', field: 'note', minWidth: 250 },
  ]

  getDealColumns = () => [
    { headerName: 'Päivämäärä', field: 'date', minWidth: 130 },
    {
      headerName: 'Kauppatunnus',
      field: 'dealId',
      minWidth: 130,
      valueGetter: (value) => value ? value.substring(0, 8) : '-',
    },
    {
      headerName: 'Tyyppi',
      field: 'type',
      minWidth: 100,
      valueGetter: (value) => value === 'buy' ? 'Osto' : value === 'sell' ? 'Myynti' : value || '-',
    },
    {
      headerName: 'Osakkeet',
      field: 'shareNumbers',
      minWidth: 200,
      valueGetter: (value) => this.formatShareRange(value),
    },
    { headerName: 'Osakkeita', field: 'shareCount', minWidth: 100 },
    {
      headerName: 'Osakelajit',
      field: 'classId',
      minWidth: 150,
      valueGetter: (value) => this.getClassName(value),
    },
    {
      headerName: 'Myyjä',
      field: 'fromId',
      minWidth: 200,
      valueGetter: (value) => this.getHolderName(value),
    },
    {
      headerName: 'Ostaja',
      field: 'toId',
      minWidth: 200,
      valueGetter: (value) => this.getHolderName(value),
    },
    {
      headerName: 'Hinta/osake (€)',
      field: 'price',
      minWidth: 130,
      valueGetter: (value) => value != null ? value : '-',
    },
    {
      headerName: 'Yhteensä (€)',
      field: 'dealTotal',
      minWidth: 130,
      valueGetter: (value, row) => {
        const price = row.price != null ? Number(row.price) : 0
        return price * row.shareCount
      },
    },
    { headerName: 'Muistiinpano', field: 'note', minWidth: 250 },
  ]

  render() {
    if (!this.state.enabled) {
      return (
        <div className='content'>
          <Row>
            <Col md='12'>
              <ShareRegistryInfo onSubmit={this.enableModule} saving={this.state.saving} />
            </Col>
          </Row>
        </div>
      )
    }

    if (this.state.loading) {
      return (
        <div className='content'>
          <Row>
            <Col>Ladataan...</Col>
          </Row>
        </div>
      )
    }

    const { activeTab, transactions, deals } = this.state

    return (
      <div className='content'>
        <Row className='mb-2'>
          <Col>
            <Tabs value={activeTab} onChange={this.handleTabChange}>
              <Tab label='Kaupat' />
              <Tab label='Per osake' />
            </Tabs>
          </Col>
        </Row>

        <Row>
          <Col xs='12'>
            <div style={{ display: 'flex', minHeight: 'calc(100vh - 250px)' }}>
              <div style={{ flexGrow: 1 }}>
                {activeTab === 0 && (
                  <DataGrid
                    rows={deals}
                    columns={this.getDealColumns()}
                    density='compact'
                    slots={{ toolbar: ExportToolbar }}
                    slotProps={{ toolbar: { suffix: 'kaupat' } }}
                  />
                )}
                {activeTab === 1 && (
                  <DataGrid
                    rows={transactions}
                    columns={this.getTransactionColumns()}
                    density='compact'
                    slots={{ toolbar: ExportToolbar }}
                    slotProps={{ toolbar: { suffix: 'tapahtumat' } }}
                  />
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    )
  }
}

export default withFirebase(ShareRegistryTransactionsContainer)
